<template>
  <div class="text-start">
    <!-- Form is not yet submitted -->
    <b-form v-if="!formIsSubmitted" @submit.prevent="onSubmit">
      <!-- Section: Personal Data -->
      <FormSection :title="$t('Personal data')" icon="fa-user">
        <b-row>
          <b-col md="6">
            <b-form-group class="mb-3">
              <label for="first-name-input">{{ $t("First name") }}</label>
              <b-input
                id="first-name-input"
                v-model="registerData.firstName"
                type="text"
                :placeholder="$t('Enter first name')"
                required
                autofocus
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group class="mb-3">
              <label for="last-name-input">{{ $t("Last name") }}</label>
              <b-input
                id="last-name-input"
                v-model="registerData.lastName"
                type="text"
                :placeholder="$t('Enter last name')"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group class="mb-0">
          <label for="email-address-input">{{ $t("E-mail address") }}</label>
          <b-input
            id="email-address-input"
            v-model="registerData.emailAddress"
            type="email"
            :placeholder="$t('Enter e-mail address')"
            required
          />
        </b-form-group>
      </FormSection>

      <!-- Section: Contact Data -->
      <FormSection :title="$t('Contact data')" icon="fa-phone">
        <b-row>
          <b-col :md="isParent ? 6 : 12">
            <PhoneNumberInput
              id="first-phone-number-input"
              :model-value="registerData.phoneNumber"
              :required="true"
              :container-class="isParent ? 'mb-3 mb-md-0' : 'mb-0'"
              @update:model-value="(value) => (registerData.phoneNumber = value)"
            />
          </b-col>

          <b-col v-if="isParent" md="6">
            <PhoneNumberInput
              id="second-phone-number-input"
              :model-value="registerData.phoneNumber2"
              label-text="Second phone number"
              placeholder="Enter second phone number"
              :required="false"
              container-class="mb-0"
              @update:model-value="(value) => (registerData.phoneNumber2 = value)"
            />
          </b-col>
        </b-row>
      </FormSection>

      <!-- Section: Institution Worker Data -->
      <template v-if="isInstitutionWorker">
        <FormSection :title="$t('Institution data')" icon="fa-building">
          <RegistrationInstitutionSelect
            v-model="registerData.institutionId"
            registration-user-type="institution_worker"
            :locked-institution-id="lockedInstitution?.id"
            :locked-institution-name="lockedInstitution?.name"
          />

          <b-form-group v-if="filteredByInstitutionClassroomsOptions.length > 1" class="mb-3">
            <Multiselector
              id="classroom-select"
              :model-value="registerData.classroomId"
              :options="filteredByInstitutionClassroomsOptions"
              :placeholder="$t('Select classroom')"
              :main-input-label="$t('Classroom')"
              :multiple="false"
              :tooltip="$t('Optional field')"
              @update:model-value="(value) => (registerData.classroomId = value)"
            />
          </b-form-group>

          <b-row>
            <b-col v-if="institutionWorkersGroupsOptions.length > 1" md="6">
              <b-form-group class="mb-3">
                <Multiselector
                  id="institutions-workers-group-select"
                  :model-value="registerData.institutionWorkersGroupId"
                  :options="institutionWorkersGroupsOptions"
                  :placeholder="$t('Select institution workers group')"
                  :main-input-label="$t('Institution workers group')"
                  :multiple="false"
                  :tooltip="$t('Optional field')"
                  @update:model-value="(value) => (registerData.institutionWorkersGroupId = value)"
                />
              </b-form-group>
            </b-col>

            <b-col :md="institutionWorkersGroupsOptions.length > 1 ? 6 : 12">
              <b-form-group class="mb-3">
                <!-- Never gets personal diets (backend filters them out) -->
                <DietsSelect
                  v-model="registerData.dietId"
                  :auto-select-default="true"
                  :default-options="dietsOptions"
                  :disabled="!registerData.institutionId"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <template v-if="canChangeAllergensForCurrentDiet">
            <b-form-group v-if="canChangeAllergens" class="mb-3">
              <Multiselector
                id="allergens-selector"
                :options="allergensOptions"
                :placeholder="$t('Choose allergens')"
                :main-input-label="$t('Allergens')"
                @update:model-value="onChangeSelectedAllergens"
              />
            </b-form-group>

            <b-form-group v-if="canChangeAllergensNote" class="mb-0">
              <label for="allergies-note-textarea">
                {{ $t("Allergies note") }}
                <Tooltip :value="$t('Optional field')" />
              </label>
              <b-form-textarea
                id="allergies-note-textarea"
                v-model="registerData.allergiesNote"
                rows="3"
                :placeholder="$t('Here you can enter allergies note.')"
              />
            </b-form-group>
          </template>

          <!-- Delivery address, contact number (only for specific tenants) -->
          <b-form-group v-if="showNote" class="mb-0 mt-3">
            <label for="general-note-textarea">
              {{ $t("Delivery address, contact number") }}
              <Tooltip :value="$t('Optional field')" />
            </label>
            <b-form-textarea
              id="general-note-textarea"
              v-model="registerData.generalNote"
              rows="3"
              :placeholder="$t('Here you can enter your delivery address and contact number')"
            />
          </b-form-group>
        </FormSection>
      </template>

      <!-- Section: Parent Additional Data -->
      <template v-if="isParent">
        <FormSection :title="$t('Additional data')" icon="fa-wallet">
          <b-form-group class="mb-0">
            <label for="bank-account-number-input">
              {{ $t("Bank account number for refunds") }}
              <small class="text-lowercase text-nowrap">({{ $t("Optional field") }})</small>
            </label>
            <input
              id="bank-account-number-input"
              v-model="registerData.bankAccountNumber"
              type="text"
              class="form-control"
              :placeholder="$t('Enter bank account number for refunds')"
              pattern="^\d{2}( ?\d{4}){6}$"
              maxlength="32"
            />
          </b-form-group>
        </FormSection>
      </template>

      <!-- Section: Invoice Settings -->
      <template v-if="invoicesConfigForUsers.enableInvoicesForUsers">
        <FormSection :title="$t('Invoice settings')" icon="fa-file-invoice">
          <b-form-checkbox
            v-model="registerData.invoicesSettings.invoicesEnabled"
            switch
            class="mb-3"
          >
            {{ $t("Enable invoices") }}
          </b-form-checkbox>

          <template v-if="registerData.invoicesSettings.invoicesEnabled">
            <b-form-group class="mb-3" :label="$t('Customer type')">
              <b-form-radio-group
                v-model="registerData.invoicesSettings.customerType"
                :options="customerTypeOptions"
              />
            </b-form-group>

            <b-row v-if="registerData.invoicesSettings.customerType === 'company'">
              <b-col md="6">
                <b-form-group class="mb-3">
                  <label for="invoices-company-name-input">{{ $t("Company name") }}</label>
                  <b-input
                    id="invoices-company-name-input"
                    v-model="registerData.invoicesSettings.customerCompanyName"
                    type="text"
                    :placeholder="$t('Enter company name')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group class="mb-3">
                  <label for="invoices-vat-id-input">{{ $t("VAT ID") }}</label>
                  <b-input
                    id="invoices-vat-id-input"
                    v-model="registerData.invoicesSettings.vatId"
                    type="text"
                    :placeholder="$t('Enter VAT ID')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row v-else>
              <b-col md="6">
                <b-form-group class="mb-3">
                  <label for="invoices-first-name-input">{{ $t("First name") }}</label>
                  <b-input
                    id="invoices-first-name-input"
                    v-model="registerData.invoicesSettings.customerFirstName"
                    type="text"
                    :placeholder="$t('Enter first name')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group class="mb-3">
                  <label for="invoices-last-name-input">{{ $t("Last name") }}</label>
                  <b-input
                    id="invoices-last-name-input"
                    v-model="registerData.invoicesSettings.customerLastName"
                    type="text"
                    :placeholder="$t('Enter last name')"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="4">
                <b-form-group class="mb-3 mb-md-0">
                  <label for="invoices-street-input">{{ $t("Street") }}</label>
                  <b-input
                    id="invoices-street-input"
                    v-model="registerData.invoicesSettings.address.street"
                    type="text"
                    :placeholder="$t('Enter street')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group class="mb-3 mb-md-0">
                  <label for="invoices-city-input">{{ $t("City") }}</label>
                  <b-input
                    id="invoices-city-input"
                    v-model="registerData.invoicesSettings.address.city"
                    type="text"
                    :placeholder="$t('Enter city')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group class="mb-0">
                  <label for="invoices-post-code-input">{{ $t("Post code") }}</label>
                  <b-input
                    id="invoices-post-code-input"
                    v-model.trim="registerData.invoicesSettings.address.postCode"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    pattern="^\d{2}-\d{3}$"
                    :formatter="postCodeInputFormatter"
                    :placeholder="$t('Enter post code')"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </template>
        </FormSection>
      </template>

      <!-- Slot for children table (parent mode) -->
      <slot />

      <!-- Section: Consents -->
      <FormSection :title="$t('Consents')" icon="fa-file-contract">
        <b-form-checkbox
          class="mb-3"
          :model-value="allTermsAndConditionsAccepted"
          @update:model-value="selectAllTermsAndConditions"
        >
          <span class="fw-bolder">{{
            allTermsAndConditionsAccepted ? $t("Unselect all") : $t("Select all")
          }}</span>
        </b-form-checkbox>

        <b-form-checkbox v-model="acceptedTermsAndConditions" class="mb-2">
          <span
            v-html="
              $t('I accept the terms and conditions', {
                url: 'https://obiado.pl/regulamin',
              })
            "
          />
        </b-form-checkbox>

        <b-form-checkbox
          v-if="!!cateringTermsOfUse"
          v-model="acceptedCateringTermsAndConditions"
          class="mb-2"
        >
          <span
            v-html="
              $t('I accept the terms and conditions of catering', {
                url: cateringTermsOfUse,
              })
            "
          />
        </b-form-checkbox>

        <b-form-checkbox v-model="acceptedPrivacyPolicy" class="mb-2">
          <span v-html="$t('I accept the privacy policy')" />
        </b-form-checkbox>

        <b-form-checkbox
          v-if="showMarketingCheckboxInRegisterForm"
          v-model="registerData.acceptedMarketing"
        >
          <span
            v-html="$t('I consent to the processing of data for marketing purposes (optional)')"
          />
        </b-form-checkbox>
      </FormSection>

      <!-- Actions -->
      <div class="text-center mt-4">
        <b-button
          type="submit"
          variant="primary"
          size="lg"
          class="w-100"
          :disabled="disableSubmitButton"
        >
          <i class="fa fa-check me-2" />
          {{ $t("Register short") }}
        </b-button>
      </div>
    </b-form>

    <!-- Form is submitted -->
    <div v-if="formIsSubmitted" class="text-center py-4">
      <div class="mb-4">
        <i class="fa fa-check-circle text-success" style="font-size: 4rem" />
      </div>

      <h4 class="mb-4">
        <i class="fa fa-user-check me-2" />
        {{ $t("User registered sucessfully") }}
      </h4>

      <b-alert v-if="registrationResult" :model-value="true" variant="warning" class="text-start">
        {{
          $t(
            "Your account has been registered, but in order to use it, you need to set up your password. Please check your e-mail for the link to set up your password and activate your account.",
          )
        }}
      </b-alert>

      <p v-if="registrationResult" class="mb-4">
        {{ $t("E-mail address") }}: <strong>{{ registrationResult.email }}</strong>
      </p>

      <router-link :to="{ name: 'auth.login' }" class="btn btn-outline-primary">
        <i class="fa fa-arrow-left me-2" />
        {{ $t("Back to login") }}
      </router-link>
    </div>
  </div>
</template>

<script>
import Multiselector from "@/components/Multiselector.vue";
import { IS_DEVELOP, IS_RPK, IS_POLKA, IS_SMACZNEGO } from "@/config";
import { computed } from "vue";
import { useStore as useVuexStore } from "vuex";
import { mapMutations, mapGetters } from "vuex";
import {
  allowChangingAllergens,
  allowChangingAllergensNote,
} from "@/modules/settings/queries/tenantSettingsSelectors";
import RegistrationInstitutionSelect from "@/modules/institutions/components/RegistrationInstitutionSelect.vue";
import DietsSelect from "@/modules/diets/components/DietsSelect.vue";
import Tooltip from "@/components/Tooltip.vue";
import PhoneNumberInput from "@/modules/parents/components/PhoneNumberInput.vue";
import FormSection from "@/components/Form/FormSection.vue";
import { postCodeInputFormatter } from "@/lib/postCode";

export default {
  name: "RegisterForm",

  components: {
    PhoneNumberInput,
    Multiselector,
    RegistrationInstitutionSelect,
    DietsSelect,
    Tooltip,
    FormSection,
  },

  props: {
    // eslint-disable-next-line vue/require-default-prop
    submit: Function,
    allergensOptions: {
      type: Array,
      default: () => [],
      required: false,
    },
    classroomsOptions: {
      type: Array,
      default: () => [],
      required: false,
    },
    institutionWorkersGroupsOptions: {
      type: Array,
      default: () => [],
      required: false,
    },
    cateringTermsOfUse: {
      type: String,
      default: null,
      required: false,
    },
    registerAs: {
      type: String,
      default: null,
      required: true,
    },
    invoicesConfigForUsers: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    lockedInstitution: {
      type: Object,
      default: null,
      required: false,
    },
  },

  emits: ["submit", "changeSelectedUserType"],

  setup() {
    const store = useVuexStore();

    const canChangeAllergens = computed(() => {
      const isParent = !!store.getters["auth/isParent"];

      if (IS_SMACZNEGO && isParent) {
        return false;
      }

      const canEditChildren = !!store.getters["auth/canEditChildren"];

      if (canEditChildren && !isParent) {
        return true;
      }

      return allowChangingAllergens.value;
    });

    const canChangeAllergensNote = computed(() => {
      const isParent = !!store.getters["auth/isParent"];

      const canEditChildren = !!store.getters["auth/canEditChildren"];

      if (canEditChildren && !isParent) {
        return true;
      }

      return allowChangingAllergensNote.value;
    });

    const showMarketingCheckboxInRegisterForm = computed(() => IS_POLKA);

    return { canChangeAllergens, canChangeAllergensNote, showMarketingCheckboxInRegisterForm };
  },

  data() {
    return {
      registerData: {
        emailAddress: null,
        firstName: null,
        lastName: null,
        institutionId: null,
        classroomId: null,
        institutionWorkersGroupId: null,
        dietId: null,
        cardNumber: null,
        allergies: null,
        allergiesNote: null,
        bankAccountNumber: null,
        phoneNumber: null,
        phoneNumber2: null,
        invoicesSettings: {
          address: {
            street: "",
            city: "",
            postCode: "",
          },
          customerType: "individual",
          customerCompanyName: "",
          customerFirstName: "",
          customerLastName: "",
          invoicesEnabled: false,
          vatId: "",
        },
        generalNote: null,
        acceptedMarketing: false,
      },
      formIsSubmitted: false,
      acceptedTermsAndConditions: false,
      acceptedCateringTermsAndConditions: false,
      acceptedPrivacyPolicy: false,
      registrationResult: null,
      formIsSubmitting: false,
      userType: null,
      dietsOptions: [],
    };
  },

  computed: {
    ...mapGetters({}),

    customerTypeOptions() {
      return [
        { text: this.$t("Individual"), value: "individual" },
        { text: this.$t("Company"), value: "company" },
      ];
    },

    allTermsAndConditionsAccepted() {
      return (
        this.acceptedTermsAndConditions &&
        this.acceptedPrivacyPolicy &&
        (!this.cateringTermsOfUse || this.acceptedCateringTermsAndConditions)
      );
    },

    disableSubmitButton() {
      if (
        !this.acceptedTermsAndConditions ||
        (this.cateringTermsOfUse && !this.acceptedCateringTermsAndConditions) ||
        !this.acceptedPrivacyPolicy ||
        this.formIsSubmitting
      ) {
        return true;
      }

      return false;
    },

    filteredByInstitutionClassroomsOptions() {
      return this.classroomsOptions.filter(
        (option) =>
          option.value === null || option.institutionId === this.registerData.institutionId,
      );
    },

    disableUserTypeSelection() {
      return window.location.href.includes("quchenka.obiado.pl");
    },

    userTypes() {
      // TODO: Get this values from backend in the future
      return [
        { value: "parent", text: this.$t("Parent") },
        { value: "institution_worker", text: this.$t("Institution worker") },
      ];
    },

    showNote() {
      if (IS_DEVELOP && this.userType === "institution_worker") {
        return true;
      }

      // Show only for "Obiad do domu" institution in RPK
      if (
        IS_RPK &&
        this.userType === "institution_worker" &&
        this.registerData.institutionId === 79
      ) {
        return true;
      }

      return false;
    },

    isInstitutionWorker() {
      return this.userType === "institution_worker";
    },

    isParent() {
      return this.userType === "parent";
    },

    canChangeAllergensForCurrentDiet() {
      if (!this.registerData.dietId) {
        return false;
      }

      // dietsOptions comes from the unauthenticated register endpoint, not the diets/options store.
      const diet = this.dietsOptions.find((option) => option.value === this.registerData.dietId);

      return !!diet?.allowChangingAllergens;
    },
  },

  watch: {
    registerAs: {
      handler: function (newValue) {
        if (newValue) {
          this.userType = newValue;

          this.onChangeUserType(this.userType);
        }
      },
      immediate: true,
    },

    "registerData.institutionId"(institutionId) {
      this.registerData.classroomId = null;

      if (this.isInstitutionWorker) {
        this.fetchDietsOptions(institutionId);
      }
    },

    canChangeAllergensForCurrentDiet(value) {
      if (!value) {
        this.clearAllergensData();
      }
    },
  },

  beforeMount() {
    // TODO dlaczego na sztywno :(
    if (IS_POLKA) {
      this.userType = "parent";
    }

    this.onChangeUserType(this.userType);
  },

  methods: {
    ...mapMutations({
      showNotification: "notifications/showNotification",
    }),

    postCodeInputFormatter,

    async onSubmit() {
      this.formIsSubmitting = true;

      if (!this.isFormValid()) {
        this.formIsSubmitting = false;

        return;
      }

      try {
        this.registrationResult = await this.submit(this.registerData, this.userType);
      } catch {
        this.formIsSubmitting = false;

        return;
      }

      this.formIsSubmitted = true;

      this.clearForm();

      this.formIsSubmitting = false;
    },

    isFormValid() {
      if (this.userType === "parent") {
        if (
          !this.registerData.emailAddress ||
          !this.registerData.firstName ||
          !this.registerData.lastName ||
          !this.registerData.phoneNumber
        ) {
          this.showNotification({
            type: "error",
            text: this.$t(
              "In order to create parent you must fill up fields: email address, first name, last name, and phone number.",
            ),
          });

          return false;
        }
      } else {
        if (
          !this.registerData.emailAddress ||
          !this.registerData.firstName ||
          !this.registerData.lastName ||
          !this.registerData.institutionId ||
          !this.registerData.dietId ||
          !this.registerData.phoneNumber
        ) {
          this.showNotification({
            type: "error",
            text: this.$t(
              "In order to create institution worker you must fill up fields: email address, first name, last name, institution, diet and phone number.",
            ),
          });

          return false;
        }
      }

      return true;
    },

    clearForm() {
      this.registerData.emailAddress = null;
      this.registerData.firstName = null;
      this.registerData.lastName = null;
      this.registerData.institutionId = null;
      this.registerData.classroomId = null;
      this.registerData.institutionWorkersGroupId = null;
      this.registerData.dietId = null;
      this.registerData.cardNumber = null;
      this.registerData.allergies = null;
      this.registerData.allergiesNote = null;
      this.registerData.bankAccountNumber = null;
      this.registerData.phoneNumber = null;
      this.registerData.phoneNumber2 = null;
      this.registerData.generalNote = null;
      this.registerData.invoicesSettings = {
        address: {
          street: "",
          city: "",
          postCode: "",
        },
        customerType: "individual",
        customerCompanyName: "",
        customerFirstName: "",
        customerLastName: "",
        invoicesEnabled: false,
        vatId: "",
      };
      this.registerData.acceptedMarketing = false;
      this.acceptedTermsAndConditions = false;
      this.acceptedCateringTermsAndConditions = false;
      this.acceptedPrivacyPolicy = false;
    },

    onChangeSelectedAllergens(allergens) {
      this.registerData.allergies = allergens;
    },

    onChangeUserType(userType) {
      if (userType === "institution_worker") {
        if (this.registerData.institutionId) {
          this.fetchDietsOptions(this.registerData.institutionId);
        }
      }

      this.$emit("changeSelectedUserType", userType);
    },

    async fetchDietsOptions(institutionId) {
      const criteria = {};

      if (institutionId) {
        criteria.institutionId = institutionId;
      }

      this.dietsOptions = await this.$store.dispatch("auth/getDietsRegisterOptions", criteria);
    },

    clearAllergensData() {
      this.registerData.allergies = null;
      this.registerData.allergiesNote = null;
    },

    selectAllTermsAndConditions(value) {
      this.acceptedTermsAndConditions = value;
      this.acceptedPrivacyPolicy = value;

      if (this.cateringTermsOfUse) {
        this.acceptedCateringTermsAndConditions = value;
      }

      if (this.showMarketingCheckboxInRegisterForm) {
        this.registerData.acceptedMarketing = value;
      }
    },
  },
};
</script>
